import React, { useState, useEffect } from 'react'
import OrgLogo from '../../common/components/Logo/OrganizationLogo'
import { portalAccentGhostButtonStyle, useTheme, useThemeColors } from '../../common/ThemeContext'
import { Button, Card, Input, Typography, Switch, Form, message } from 'antd'
import axios from 'axios'
import { config } from '../../Configs/firebase.config'
import FooterAllRights from '../../common/components/Footer/FooterAllRights'
import CheckinCheckoutHeader from '../../common/components/Header/CheckinCheckoutHeader'
import { httpGet } from '../../common/http.service'

const { Title, Text } = Typography
const { TextArea } = Input

const apiEndpoint = `${config.baseUrl}/api/v1/client/checkout`

function CheckOutForm() {
	const { theme } = useTheme()
	const themeColors = useThemeColors()
	const { primary } = themeColors
	const ghostBtn = portalAccentGhostButtonStyle(theme, primary)
	const [staffId, setStaffId] = useState<string>('')
	const [isNightShiftEmg, setIsNightShiftEmg] = useState<boolean>(false)
	const [checkoutReason, setCheckoutReason] = useState<string>('')
	const [loading, setLoading] = useState(false)
	const [checkOutMsg, setCheckOutMsg] = useState<{ title: string; description: string }>({
		title: '',
		description: '',
	})
	const [showCheckOutMsg, setShowCheckOutMsg] = useState(false)
	const [orgLocation, setOrgLocation] = useState<{ latitude: string; longitude: string } | null>(
		null,
	)

	useEffect(() => {
		fetchOrganizationLocation()
	}, [])

	const fetchOrganizationLocation = async () => {
		try {
			const response: any = await httpGet('/api/v1/admin/org')
			if (response?.data) {
				setOrgLocation({
					latitude: response.data.latitude?.toString() || '',
					longitude: response.data.longitude?.toString() || '',
				})
			}
		} catch (error: any) {
			console.error('Failed to fetch organization location:', error)
			message.error('Failed to load organization information')
		}
	}

	const handleNightShiftToggle = (checked: boolean) => {
		setIsNightShiftEmg(checked)
	}

	const checkOut = async () => {
		if (!staffId) return
		if (!orgLocation) {
			message.error('Organization location not available')
			return
		}
		setLoading(true)
		const body = {
			staffId: `${staffId}`,
			isExtraNightShift: isNightShiftEmg,
			checkinReason: '',
			checkoutReason: `${checkoutReason}`,
			latitude: orgLocation.latitude,
			longitude: orgLocation.longitude,
		}

		try {
			const res = await axios.post(apiEndpoint, body)
			setCheckOutMsg({
				title: 'Check Out Successful',
				description: res.data.data.message,
			})
			setShowCheckOutMsg(true)
		} catch (error) {
			// Narrowing the error type
			if (axios.isAxiosError(error)) {
				setCheckOutMsg({
					title: 'Check Out Failed',
					description: `${
						error.response?.data?.message[0] || 'An error occurred'
					}. Staff ID: ${staffId}.`,
				})
			} else {
				setCheckOutMsg({
					title: 'Check Out Failed',
					description: 'An unexpected error occurred.',
				})
			}
			setShowCheckOutMsg(true)
		} finally {
			setLoading(false)
		}
	}

	return (
		<>
			<CheckinCheckoutHeader />
			{!showCheckOutMsg ? (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: 'calc(100vh - 120px)',
					}}
				>
					<Card
						style={{
							width: '90%',
							maxWidth: '400px',
							textAlign: 'center',
							borderRadius: '12px',
							boxShadow: themeColors.cardShadow,
							background: themeColors.bg,
							borderColor: themeColors.border,
						}}
					>
						<OrgLogo />
						<Title
							level={2}
							style={{
								color: primary,
								fontFamily: 'Times New Roman',
								marginTop: '20px',
							}}
						>
							Check Out
						</Title>
						<Form layout='vertical'>
							{/* Staff ID Input */}
							<Form.Item label='Staff ID'>
								<Input
									placeholder='Input staff ID here'
									value={staffId}
									onChange={(e) => setStaffId(e.target.value)}
								/>
							</Form.Item>

							{/* Night Shift Toggle */}
							<Form.Item label='Night Shift Emergency'>
								<Switch
									checked={isNightShiftEmg}
									onChange={handleNightShiftToggle}
								/>
							</Form.Item>

							{/* Check Out Button */}
							<Form.Item>
								<Button
									loading={loading}
									disabled={!staffId}
									onClick={checkOut}
									style={{ ...ghostBtn, width: '100%' }}
								>
									Check Out
								</Button>
							</Form.Item>
						</Form>
						<FooterAllRights />
					</Card>
				</div>
			) : (
				<Card
					style={{
						margin: '20px',
						textAlign: 'center',
						background: themeColors.bg,
						borderColor: themeColors.border,
					}}
				>
					<Title
						level={4}
						style={{ color: checkOutMsg.title.includes('Failed') ? 'red' : 'green' }}
					>
						{checkOutMsg.title}
					</Title>
					<Text style={{ color: themeColors.text }}>{checkOutMsg.description}</Text>
					{checkOutMsg.description.includes('You are early') && (
						<Form layout='vertical' style={{ marginTop: '20px' }}>
							<Form.Item label='Reason for Early Check Out'>
								<TextArea
									placeholder='Write your reason here'
									value={checkoutReason}
									onChange={(e) => setCheckoutReason(e.target.value)}
								/>
							</Form.Item>
							<Button
								disabled={!checkoutReason}
								onClick={checkOut}
								style={{ ...ghostBtn, width: '100%' }}
							>
								Submit
							</Button>
						</Form>
					)}
					<FooterAllRights />
				</Card>
			)}
		</>
	)
}

export default CheckOutForm
